import * as React from 'react';
import styled from 'styled-components';
import { useState } from 'react';
import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import Loader from '../../components/LoginSso/loader';
import Error from '../../components/LoginSso/EpicDisney/error';
import requestAuthorizationCode from '../../components/LoginSso/EpicDisney/request-authorization-code';
import useSubmitAuthorizationCode from '../../components/LoginSso/EpicDisney/hooks/use-submit-authorization-code';
import useGetPatientDetails from '../../components/LoginSso/EpicDisney/hooks/use-get-patient-details';
import useRecognizePatientOnBackend from '../../components/LoginSso/EpicDisney/hooks/use-recognize-patient-on-backend';
import useConnectPatientToS3 from '../../components/LoginSso/hooks/use-connect-patient-to-s3';
import EpicDisneyConfig from '../../components/LoginSso/EpicDisney/config';
import RecognizePatientResultCode from '../../components/LoginSso/EpicDisney/types/recognize-patient-result-code';
import CreatePatientForm from '../../components/LoginSso/EpicDisney/create-patient-form';

const Container = styled.div`
  display: flex;
  justify-content: center;
`;

const STEPS = {
  initializing: 'initializing',
  waitingForLoginButtonClick: 'waitingForLoginButtonClick',
  requestingAuthorizationCode: 'requestingAuthorizationCode',
  submittingAuthorizationCode: 'submittingAuthorizationCode',
  retrievingPatientDetails: 'retrievingPatientDetails',
  recognizingPatientOnBackend: 'recognizingPatientOnBackend',
  creatingNewPatient: 'creatingNewPatient',
  connectingPatientToS3: 'connectingPatientToS3',
  loggedIn: 'loggedIn',
  failed: 'failed'
};

export default function LoginSsoEpicDisney() {
  const history = useHistory();

  const [step, setStep] = useState(STEPS.initializing);
  const [errorText, setErrorText] = useState('');

  const urlParams = new URLSearchParams(window.location.search);
  const launchToken = urlParams.get('launch');
  const authorizationCode = urlParams.get('code');
  const [submitAuthorizationCodeResponse, submitAuthorizationCode] = useSubmitAuthorizationCode();
  const [getPatientDetailsResponse, getPatientDetails] = useGetPatientDetails();
  const [
    recognizePatientOnBackendResponse,
    recognizePatientOnBackend
  ] = useRecognizePatientOnBackend();
  const [connectPatientToS3Response, connectPatientToS3] = useConnectPatientToS3();

  useEffect(() => {
    if (step == STEPS.initializing) {
      if (!authorizationCode) {
        // when there is no authorization ?code= in URL, the login attempt only started
        // the login attempt can be invoked in two ways:
        // 1. standalone launch, you visit /login-sso/epic-disney URL (no URL params)
        // 2. embedded launch, in My Chart app, you click Kindbody, and you are redirected to
        // /login-sso/epic-disney?iss=<value>&launch=<value>
        // in both cases, we need to retrieve the authorization code by making a full HTTP redirect to Mychart
        // in standalone launch, we will be asked to login to MyChart app
        // in embedded launch, thanks to the launch token, we will be already recognized as 'logged in'
        setStep(STEPS.requestingAuthorizationCode);
      } else {
        // in both cases, we are eventually redirected back here with /login-sso/epic-disney?code=<value>
        setStep(STEPS.submittingAuthorizationCode);
      }
    }
    if (step == STEPS.submittingAuthorizationCode && submitAuthorizationCodeResponse.finished) {
      if (submitAuthorizationCodeResponse.idtoken) setStep(STEPS.retrievingPatientDetails);
      else if (submitAuthorizationCodeResponse.error) {
        setStep(STEPS.failed);
        setErrorText(
          'Failed to submit authorization code: ' + submitAuthorizationCodeResponse.error
        );
      }
    }
    if (step == STEPS.retrievingPatientDetails && getPatientDetailsResponse.finished) {
      if (getPatientDetailsResponse.error) {
        setStep(STEPS.failed);
        setErrorText(
          'Failed to retrieve your details from MyChart: ' + getPatientDetailsResponse.error
        );
      } else setStep(STEPS.recognizingPatientOnBackend);
    }
    if (step == STEPS.recognizingPatientOnBackend && recognizePatientOnBackendResponse.finished) {
      if (
        recognizePatientOnBackendResponse.resultCode == RecognizePatientResultCode.PatientRecognized
      ) {
        setStep(STEPS.connectingPatientToS3);
      } else if (
        recognizePatientOnBackendResponse.resultCode ==
          RecognizePatientResultCode.PatientNotRecognized ||
        recognizePatientOnBackendResponse.resultCode == RecognizePatientResultCode.Failed
      ) {
        setStep(STEPS.failed);
        setErrorText((recognizePatientOnBackendResponse.errors as []).join('. '));
      } else if (
        recognizePatientOnBackendResponse.resultCode ==
        RecognizePatientResultCode.PatientNotRecognizedButCanBeCreated
      ) {
        setStep(STEPS.creatingNewPatient);
      } else if (recognizePatientOnBackendResponse.errors) {
        setStep(STEPS.failed);
        setErrorText(
          'Failed to find you in Kindbody database: ' +
            recognizePatientOnBackendResponse.errors.join('. ')
        );
      } else setStep(STEPS.connectingPatientToS3);
    }
    if (step == STEPS.connectingPatientToS3 && connectPatientToS3Response.finished) {
      if (!connectPatientToS3Response.succeeded) {
        console.warn(
          'Failed to connect patient to AWS S3. Patient will be unable to upload/download S3 hosted files.'
        );
        console.warn(connectPatientToS3Response.error);
      }
      // no matter if we managed to connect patient to S3 or not, we allow the patient to use PP app
      setStep(STEPS.loggedIn);
    }
  }, [
    step,
    submitAuthorizationCodeResponse,
    getPatientDetailsResponse,
    recognizePatientOnBackendResponse,
    connectPatientToS3Response
  ]);

  useEffect(() => {
    if (step == STEPS.requestingAuthorizationCode) {
      // if this is login invoked in MyChart, we have launch token
      // if this is standalone launch (from button in our app), we don't have the launch token (we pass null)
      // this is doing full page redirect, we leave React here
      requestAuthorizationCode(launchToken);
    } else if (step == STEPS.submittingAuthorizationCode) {
      submitAuthorizationCode(authorizationCode);
    } else if (step == STEPS.retrievingPatientDetails) {
      // we don't reall need this here, but for now I keep it for demo purposes
      getPatientDetails(
        submitAuthorizationCodeResponse.accessToken,
        submitAuthorizationCodeResponse.patientFhirId
      );
    } else if (step == STEPS.recognizingPatientOnBackend) {
      recognizePatientOnBackend(
        submitAuthorizationCodeResponse.accessToken,
        submitAuthorizationCodeResponse.idtoken
      );
    } else if (step == STEPS.connectingPatientToS3) {
      // I'd rather use redux to store the token, but then in getEpicSsoIdToken (packages/kb-shared/src/utilities/api.ts)
      // we cannot use redux to retrieve the value
      window.sessionStorage.setItem('epicSsoIdToken', submitAuthorizationCodeResponse.idtoken);

      // this string is very sensitive
      // epicproxy-np.et1052.epichosted.com/FHIRProxy/oauth2 : works fine, AWS will recognize it
      // https://epicproxy-np.et1052.epichosted.com/FHIRProxy/oauth2 : Invalid login token. Issuer doesn't match providerName
      // epicproxy-np.et1052.epichosted.com/FHIRProxy : Invalid login token. Issuer doesn't match providerName
      const identityProvider = EpicDisneyConfig.epicUrl.replace('https://', '') + '/oauth2';
      const patientName = `Disney patient FHIR=${submitAuthorizationCodeResponse.patientFhirId}`;
      connectPatientToS3(identityProvider, submitAuthorizationCodeResponse.idtoken, patientName);
    } else if (step == STEPS.loggedIn) history.push('/');
  }, [step]);

  const showLoader =
    step == STEPS.requestingAuthorizationCode ||
    step == STEPS.submittingAuthorizationCode ||
    step == STEPS.retrievingPatientDetails ||
    step == STEPS.recognizingPatientOnBackend ||
    step == STEPS.connectingPatientToS3;

  return (
    <Container>
      <div>
        {showLoader && <Loader labelText="Please wait, we are logging you in." />}
        {step == STEPS.loggedIn && (
          <div>You are successfully logged in. Redirecting you to dashboard</div>
        )}
        {step == STEPS.creatingNewPatient && (
          <CreatePatientForm
            idtoken={submitAuthorizationCodeResponse.idtoken as string}
            accessToken={submitAuthorizationCodeResponse.accessToken as string}
            onPatientSuccessfullyCreated={() => setStep(STEPS.connectingPatientToS3)}
          />
        )}
        {step == STEPS.failed && <Error text={errorText} />}
      </div>
    </Container>
  );
}
